import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LiquidEther from '../components/LiquidEther';
import TourBookingPanel from '../components/TourBookingPanel';
import { useApiResource } from '../hooks/useApiResource';
import {
  BlockRenderer,
  getBlockValue,
  HeroBlock,
  MarqueeBlock,
  normalizeTitle,
} from '../components/dynamic/blocks';

export default function DynamicPage({ slugOverride, fallback }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const { i18n } = useTranslation();
  const { data: page, loading, error } = useApiResource(`/pages-dynamic/${slug}`);
  const isHome = slug === 'home';
  const showLiquidBackground = !isHome;

  const blocks = useMemo(() => page?.blocks || [], [page]);

  if (loading) {
    return (
      <div className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-white/60">Loading...</div>
      </div>
    );
  }

  if (!page) {
    if (fallback) {
      return fallback;
    }
    return (
      <div className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-white/60">
          {error ? 'Failed to load page.' : 'Page not found.'}
        </div>
      </div>
    );
  }

  const firstBlock = blocks[0];
  const hasFullBleedHero = page?.full_width && firstBlock?.type === 'hero' && firstBlock?.full_bleed;
  const allowHero = page?.show_hero === true;
  const visibleBlocks = isHome || allowHero
    ? blocks
    : blocks.filter((block) => block?.type !== 'hero');
  const marqueeBlock = isHome
    ? visibleBlocks.find((block) => block?.type === 'marquee')
    : null;
  const blocksToRender = isHome && hasFullBleedHero && marqueeBlock
    ? visibleBlocks.filter((block) => block !== marqueeBlock)
    : visibleBlocks;
  const filteredBlocks = isHome
    ? blocksToRender.filter((block) => block?.type !== 'stats')
    : blocksToRender;
  const pageTitle = i18n.language === 'en' && page?.title_en ? page.title_en : page?.title;
  const firstBlockTitle = visibleBlocks[0]
    ? getBlockValue(visibleBlocks[0], 'title', i18n.language)
      || getBlockValue(visibleBlocks[0], 'heading', i18n.language)
    : null;
  const hidePageTitle = normalizeTitle(pageTitle) && normalizeTitle(pageTitle) == normalizeTitle(firstBlockTitle);

  return (
    <div className={`pb-24 ${hasFullBleedHero ? 'pt-0' : 'pt-28'} relative`}>
      {showLiquidBackground && (
        <div className="fixed inset-0 z-0">
          <LiquidEther
            colors={["#e70d0d", "#850000", "#ea6d57"]}
            mouseForce={20}
            cursorSize={120}
            resolution={0.5}
            dt={0.014}
            BFECC
            isViscous={false}
            viscous={30}
            iterationsViscous={32}
            iterationsPoisson={32}
            isBounce={false}
            autoDemo
            autoSpeed={0.5}
            autoIntensity={2.2}
            takeoverDuration={0.25}
            autoResumeDelay={1000}
            autoRampDuration={0.6}
            style={{ width: '100%', height: '100%' }}
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
      )}
      <div className="space-y-12 relative z-10">
        {!page.hide_title && !hidePageTitle && (
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h1 className="text-4xl md:text-6xl font-black font-heading text-white tracking-tight">
              {i18n.language === 'en' && page.title_en ? page.title_en : page.title}
            </h1>
          </div>
        )}

        {filteredBlocks.map((block, index) => {
          const isFullBleed = Boolean(block.full_bleed) || block.type === 'marquee';
          if (isHome && hasFullBleedHero && block === firstBlock) {
            const hero = (
              <HeroBlock
                block={block}
                lang={i18n.language}
                useGlitch={block.glitch_enabled !== false}
                bottomSlot={marqueeBlock ? <MarqueeBlock block={marqueeBlock} lang={i18n.language} /> : null}
              />
            );
            if (page.full_width && isFullBleed) {
              return (
                <div key={block.id || block._id || index} className="w-full">
                  {hero}
                </div>
              );
            }
            return (
              <div key={block.id || block._id || index} className="max-w-7xl mx-auto px-4 md:px-8">
                {hero}
              </div>
            );
          }
          if (page.full_width && isFullBleed) {
            return (
              <div key={block.id || block._id || index} className="w-full">
                <BlockRenderer block={block} lang={i18n.language} />
              </div>
            );
          }
          return (
            <div key={block.id || block._id || index} className="max-w-7xl mx-auto px-4 md:px-8">
              <BlockRenderer block={block} lang={i18n.language} />
            </div>
          );
        })}

        {slug?.startsWith('tours-') ? <TourBookingPanel page={page} slug={slug} /> : null}
      </div>
    </div>
  );
}
